import {
    Box,
    Typography,
    Tabs,
    Tab,
    Alert,
    AlertTitle,
    Button,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Settings } from '@/shared/ui/Icons'

import {
    useState,
    useEffect,
    SyntheticEvent,
    ReactNode,
    useCallback,
} from 'react'
import { useSharedStyles } from '@/pages/Settings/useSharedStyles'
import { DeviceDeactivatesModal } from '@/pages/Settings/sections/DeviceDeactivatesModal'
import { DeviceSetAliasModal } from '@/pages/Settings/sections/DeviceSetAliasModal'
import { AboutApplicationSection } from './sections/AboutApplicationSection'
import { TermsAndConditionsSection } from './sections/TermsAndConditionsSection'
import { DevicesSection } from '@/pages/Settings/sections/DevicesSection'
import { PinSection } from '@/pages/Settings/sections/PinSection'
import { HelpSection } from '@/pages/Settings/sections/HelpSection'
import { NewsletterSection } from './sections/NewsletterSection'
import { getWithAuth, putWithAuth } from '@/api/httpRequests'
import {
    devicesEndpoint,
    nameDeviceEndpoint,
    revokeDeviceEndpoint,
    newPinEndpoint,
} from '@/api/apiConstants'
import { DevicesResultType } from '@/types/settings/DevicesResultType'
import { defaultProfile } from '@/shared/constants'
import { useAppStore, useVisibilityStore } from '@/shared/store'
import {
    getFromLocalStorage,
    removeFromLocalStorage,
} from '@/shared/localStorage/localStorageRepository'

interface TabPanelProps {
    children?: ReactNode
    index: number
    value: number
}

const a11yProps = (index: number) => ({
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
})

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`settings-tabpanel-${index}`}
        aria-labelledby={`settings-tab-${index}`}
        {...other}>
        {value === index && <Box>{children}</Box>}
    </div>
)

const SettingsPage = () => {
    const [tab, setTab] = useState<number>(0)
    const [devices, setDevices] = useState<DevicesResultType>([])
    const [isModalDeviceDeactivateOpen, setIsModalDeviceDeactivateOpen] =
        useState<boolean>(false)
    const [isModalDeviceAliasOpen, setIsModalDeviceAliasOpen] =
        useState<boolean>(false)
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(
        null
    )
    const [selectedDeviceAlias, setSelectedDeviceAlias] = useState<
        string | null | undefined
    >(null)
    const {
        setIsUserLoggedIn,
        setActualProfile,
        setProfiles,
        setDevicesLimitExceeded,
        devicesLimitExceeded,
    } = useAppStore()
    const { setLoginContentVisible } = useVisibilityStore()
    const { t: translate } = useTranslation()
    const {
        titleStyle,
        titleContainer,
        container,
        sectionStyle,
        iconContainer,
        dataContainer,
        tabStyle,
    } = useSharedStyles()

    useEffect(() => {
        fetchData()
    }, [])

    const fetchData = async () => {
        try {
            const response = await getWithAuth(devicesEndpoint)
            setDevices(response)
        } catch (error) {
            console.error('Error fetching data:', error)
        }
    }

    const deviceDeactivate = useCallback(async (deviceId?: string | null) => {
        try {
            if (deviceId) {
                const body = JSON.stringify({
                    device_id: deviceId,
                })
                const response = await putWithAuth(
                    revokeDeviceEndpoint,
                    true,
                    body
                )

                if ('ok' === response.status) {
                    const currentDeviceId = getFromLocalStorage('device_id')
                    setDevicesLimitExceeded(false)
                    closeDeviceDeactivateModal()

                    if (currentDeviceId === deviceId) {
                        handleLogout()
                    } else {
                        await fetchData()
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error)
        }
    }, [])

    const setDeviceAlias = useCallback(
        async (deviceId?: string | null, alias?: string | null) => {
            try {
                if (deviceId && alias) {
                    const body = JSON.stringify({
                        device_id: deviceId,
                        alias,
                    })
                    const response = await putWithAuth(
                        nameDeviceEndpoint,
                        true,
                        body
                    )
                    if ('ok' === response.status) {
                        closeSetDeviceAliasModal()
                        await fetchData()
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error)
            }
        },
        []
    )

    const setNewPin = async (oldPin: string, newPin: string) => {
        try {
            const body = JSON.stringify({
                oldPin,
                newPin,
            })
            await putWithAuth(newPinEndpoint, true, body)

            return true
        } catch (error) {
            console.error('Error fetching data:', error)
        }

        return false
    }

    const handleManageDevicesClick = () => setTab(0)

    const openDeviceDeactivateModal = useCallback(
        (deviceId: string, alias?: string | null) => (
            setSelectedDeviceId(deviceId),
            setSelectedDeviceAlias(alias),
            setIsModalDeviceDeactivateOpen(true)
        ),
        []
    )

    const closeDeviceDeactivateModal = useCallback(
        () => (
            setIsModalDeviceDeactivateOpen(false),
            setSelectedDeviceId(null),
            setSelectedDeviceAlias(null)
        ),
        []
    )

    const openSetDeviceAliasModal = useCallback(
        (deviceId: string, alias?: string | null) => (
            setSelectedDeviceId(deviceId),
            setSelectedDeviceAlias(alias),
            setIsModalDeviceAliasOpen(true)
        ),
        []
    )

    const closeSetDeviceAliasModal = useCallback(
        () => (
            setIsModalDeviceAliasOpen(false),
            setSelectedDeviceId(null),
            setSelectedDeviceAlias(null)
        ),
        []
    )

    const handleLogout = () => {
        setActualProfile(defaultProfile)
        setProfiles([])
        setDevicesLimitExceeded(false)
        removeFromLocalStorage('dottv: sessionToken')
        removeFromLocalStorage('dottv: refreshToken')
        removeFromLocalStorage('dottv: lastTimeStart')
        removeFromLocalStorage('dottv:config')
        setLoginContentVisible()
        setIsUserLoggedIn(false)
    }

    return (
        <Box sx={container}>
            <Box sx={sectionStyle}>
                <Box sx={titleContainer}>
                    <Box sx={iconContainer}>
                        <Settings />
                    </Box>
                    <Typography ml="24px" sx={titleStyle}>
                        {translate('settings')}
                    </Typography>
                </Box>
                <Box sx={dataContainer}>
                    {devicesLimitExceeded && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <AlertTitle>
                                {translate('devicesLimitExceededTitle')}
                            </AlertTitle>
                            {translate('devicesLimitExceededMessage')}
                        </Alert>
                    )}
                    <DeviceDeactivatesModal
                        isModalOpen={isModalDeviceDeactivateOpen}
                        closeModal={closeDeviceDeactivateModal}
                        deviceId={selectedDeviceId}
                        alias={selectedDeviceAlias}
                        deviceDeactivate={deviceDeactivate}
                    />
                    <DeviceSetAliasModal
                        isModalOpen={isModalDeviceAliasOpen}
                        closeModal={closeSetDeviceAliasModal}
                        deviceId={selectedDeviceId}
                        alias={selectedDeviceAlias}
                        setAlias={setDeviceAlias}
                    />
                    <Tabs
                        value={tab}
                        onChange={(_event: SyntheticEvent, value: number) =>
                            setTab(value)
                        }
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        aria-label="settings tabs">
                        <Tab
                            sx={tabStyle}
                            label={translate('devices')}
                            {...a11yProps(0)}
                        />
                        <Tab
                            sx={tabStyle}
                            label={translate('pin')}
                            {...a11yProps(1)}
                        />
                        <Tab
                            sx={tabStyle}
                            label={translate('T&C')}
                            {...a11yProps(2)}
                        />
                        <Tab
                            sx={tabStyle}
                            label={translate('aboutApplicationTitle')}
                            {...a11yProps(3)}
                        />
                        <Tab
                            sx={tabStyle}
                            label={translate('help')}
                            {...a11yProps(4)}
                        />
                        <Tab
                            sx={tabStyle}
                            label={translate('vodNewsletter')}
                            {...a11yProps(5)}
                        />
                    </Tabs>
                    <Box>
                        <TabPanel value={tab} index={0}>
                            <DevicesSection
                                openDeviceDeactivateModal={
                                    openDeviceDeactivateModal
                                }
                                openSetDeviceAliasModal={
                                    openSetDeviceAliasModal
                                }
                                devices={devices}
                            />
                        </TabPanel>
                        <TabPanel value={tab} index={1}>
                            <PinSection saveNewPin={setNewPin} />
                        </TabPanel>
                        <TabPanel value={tab} index={2}>
                            <TermsAndConditionsSection />
                        </TabPanel>
                        <TabPanel value={tab} index={3}>
                            <AboutApplicationSection />
                        </TabPanel>
                        <TabPanel value={tab} index={4}>
                            <HelpSection />
                        </TabPanel>
                        <TabPanel value={tab} index={5}>
                            <NewsletterSection />
                        </TabPanel>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export { SettingsPage }
